import {
  BarChart3,
  Bell,
  BookmarkCheck,
  BookOpen,
  BringToFront,
  Columns3,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeftRight,
  ChevronsRightLeft,
  ChevronUp,
  Circle,
  TriangleAlert,
  CircleCheck,
  FileX,
  FileClock,
  SlidersHorizontal,
  Copy,
  DollarSign,
  Download,
  EllipsisVertical,
  Eye,
  EyeOff,
  Check,
  CornerDownLeft,
  FileCheck,
  FileSearch,
  FileText,
  LayoutList,
  GitCompare,
  Footprints,
  LayoutGrid,
  Haze,
  Headset,
  Hourglass,
  House,
  BookOpenText,
  Funnel,
  Info,
  KeyRound,
  Link,
  List,
  LogOut,
  Mail,
  MailPlus,
  MapPin,
  MapPinned,
  MessageSquarePlus,
  MessageSquareText,
  MessageSquareX,
  Minus,
  Boxes,
  Paperclip,
  Percent,
  Printer,
  PieChart,
  Plus,
  RefreshCw,
  Laptop,
  Rocket,
  RotateCcw,
  Save,
  Scan,
  Search,
  Send,
  Settings,
  Share2,
  Sparkles,
  Square,
  FilePen,
  Star,
  TextWrap,
  Trash2,
  TrendingDown,
  TrendingUp,
  Reply,
  PlugZap,
  Upload,
  UserCheck,
  UserCog,
  UserPen,
  UserPlus,
  Users,
  Webhook,
  X,
  Lightbulb,
  Cloud,
  Droplet,
  Box,
  Car,
  Zap,
  File,
  ListChecks,
  type LucideIcon
} from "lucide-react";
import {
  BellIcon as AnimatedBell,
  BookOpenIcon as AnimatedBookOpen,
  BoxesIcon as AnimatedBoxes,
  BringToFrontIcon as AnimatedBringToFront,
  CalendarIcon as AnimatedCalendar,
  ChartColumnIcon as AnimatedChartColumn,
  ChartPieIcon as AnimatedChartPie,
  ChevronsLeftRightIcon as AnimatedChevronsLeftRight,
  ChevronsRightLeftIcon as AnimatedChevronsRightLeft,
  CircleCheckIcon as AnimatedCircleCheck,
  CopyIcon as AnimatedCopy,
  CornerDownLeftIcon as AnimatedCornerDownLeft,
  DollarSignIcon as AnimatedDollarSign,
  DownloadIcon as AnimatedDownload,
  EllipsisVerticalIcon as AnimatedEllipsisVertical,
  EyeIcon as AnimatedEye,
  EyeOffIcon as AnimatedEyeOff,
  FileCheckIcon as AnimatedFileCheck,
  FileTextIcon as AnimatedFileText,
  GitCompareIcon as AnimatedGitCompare,
  HazeIcon as AnimatedHaze,
  HeadsetIcon as AnimatedHeadset,
  HourglassIcon as AnimatedHourglass,
  InfoIcon as AnimatedInfo,
  LightbulbIcon as AnimatedLightbulb,
  ListIcon as AnimatedList,
  LogOutIcon as AnimatedLogOut,
  MailIcon as AnimatedMail,
  MailPlusIcon as AnimatedMailPlus,
  MapPinIcon as AnimatedMapPin,
  MapPinnedIcon as AnimatedMapPinned,
  MessageSquarePlusIcon as AnimatedMessageSquarePlus,
  MessageSquareTextIcon as AnimatedMessageSquareText,
  MessageSquareXIcon as AnimatedMessageSquareX,
  MinusIcon as AnimatedMinus,
  PaperclipIcon as AnimatedPaperclip,
  PlusIcon as AnimatedPlus,
  PrinterIcon as AnimatedPrinter,
  SaveIcon as AnimatedSave,
  ScanIcon as AnimatedScan,
  SearchIcon as AnimatedSearch,
  SendIcon as AnimatedSend,
  ShareIcon as AnimatedShare,
  SparklesIcon as AnimatedSparkles,
  Trash2Icon as AnimatedTrash2,
  UploadIcon as AnimatedUpload,
  UserCheckIcon as AnimatedUserCheck,
  UserCogIcon as AnimatedUserCog,
  UserPenIcon as AnimatedUserPen,
  UserPlusIcon as AnimatedUserPlus,
  UsersIcon as AnimatedUsers,
  WebhookIcon as AnimatedWebhook,
  WrapTextIcon as AnimatedWrapText,
  XIcon as AnimatedX,
  FileSearchIcon as AnimatedFileSearch,
  SettingsIcon as AnimatedSettings,
  LayoutGridIcon as AnimatedLayoutGrid,
  ChevronLeftIcon as AnimatedChevronLeft,
  ChevronRightIcon as AnimatedChevronRight,
  ChevronUpIcon as AnimatedChevronUp,
  ChevronDownIcon as AnimatedChevronDown,
  FilePenIcon as AnimatedFilePen,
  BookmarkCheckIcon as AnimatedBookmarkCheck,
  LayoutListIcon as AnimatedLayoutList,
  CheckIcon as AnimatedCheck,
  FileClockIcon as AnimatedFileClock,
  TriangleAlertIcon as AnimatedTriangleAlert,
  PlugZapIcon as AnimatedPlugZap,
  FilterIcon as AnimatedFunnel,
  HouseIcon as AnimatedHouse,
  KeyRoundIcon as AnimatedKeyRound,
  LinkIcon as AnimatedLink,
  RocketIcon as AnimatedRocket,
  RefreshCwIcon as AnimatedRefreshCw,
  ReplyIcon as AnimatedReply,
  TrendingDownIcon as AnimatedTrendingDown,
  TrendingUpIcon as AnimatedTrendingUp,
  SlidersHorizontalIcon as AnimatedSlidersHorizontal,
  FootprintsIcon as AnimatedFootprints,
  BookOpenTextIcon as AnimatedBookOpenText,
  FileXIcon as AnimatedFileX,
  LaptopIcon as AnimatedLaptop,
  CloudIcon as AnimatedCloud,
  DropletIcon as AnimatedDroplet,
  BoxIcon as AnimatedBox,
  CarIcon as AnimatedCar,
  ZapIcon as AnimatedZap,
  FileIcon as AnimatedFile,
  ListChecksIcon as AnimatedListChecks
} from "@animateicons/react/lucide";
import type { IconHandle } from "@animateicons/react";
import type {
  ComponentPropsWithoutRef,
  ForwardRefExoticComponent,
  RefAttributes
} from "react";

/**
 * Every `@animateicons/react` glyph has the same shape — a div-wrapped svg
 * taking a numeric `size` and a `color`, with start/stop on its ref — so one
 * alias derived from the package's own typings describes all of them.
 */
export type AnimatedGlyph = ForwardRefExoticComponent<
  ComponentPropsWithoutRef<typeof AnimatedDownload> & RefAttributes<IconHandle>
>;

export interface IconEntry {
  glyph: LucideIcon;
  /**
   * The motion twin of `glyph`. Paired by comparing the two glyphs' rendered
   * path geometry, never by name — lucide aliases mean the names can differ
   * (`BarChart3`/`ChartColumn`, `Share2`/`Share`) and identical names can draw
   * different glyphs (`Settings` gains five dots, `Check` a shorter tick).
   * `glyph` stays the fallback: a row without this renders exactly as it always
   * did, and no call site can tell the two apart.
   */
  animated?: AnimatedGlyph;
  filled?: boolean;
}

export const ICON_REGISTRY = {
  accept: { animated: AnimatedCircleCheck, glyph: CircleCheck },
  add: { animated: AnimatedPlus, glyph: Plus },
  ai: { animated: AnimatedSparkles, glyph: Sparkles },
  alert: { animated: AnimatedTriangleAlert, glyph: TriangleAlert },
  attachment: { animated: AnimatedPaperclip, glyph: Paperclip },
  "blank-file": { animated: AnimatedFile, glyph: File },
  calendar: { animated: AnimatedCalendar, glyph: Calendar },
  "chart-bar": { animated: AnimatedChartColumn, glyph: BarChart3 },
  "chart-pie": { animated: AnimatedChartPie, glyph: PieChart },
  check: { animated: AnimatedCheck, glyph: Check },
  "chevron-down": { animated: AnimatedChevronDown, glyph: ChevronDown },
  "chevron-left": { animated: AnimatedChevronLeft, glyph: ChevronLeft },
  "chevron-right": { animated: AnimatedChevronRight, glyph: ChevronRight },
  "chevron-up": { animated: AnimatedChevronUp, glyph: ChevronUp },
  clear: { glyph: RotateCcw },
  "clock-check": { animated: AnimatedFileClock, glyph: FileClock },
  close: { animated: AnimatedX, glyph: X },
  collapse: { animated: AnimatedChevronsRightLeft, glyph: ChevronsRightLeft },
  columns: { glyph: Columns3 },
  compliance: { animated: AnimatedGitCompare, glyph: GitCompare },
  connect: { animated: AnimatedPlugZap, glyph: PlugZap },
  copy: { animated: AnimatedCopy, glyph: Copy },
  delete: { animated: AnimatedTrash2, glyph: Trash2 },
  docs: { animated: AnimatedBookOpen, glyph: BookOpen },
  dollar: { animated: AnimatedDollarSign, glyph: DollarSign },
  download: { animated: AnimatedDownload, glyph: Download },
  edit: { animated: AnimatedFilePen, glyph: FilePen },
  emission: { animated: AnimatedCloud, glyph: Cloud },
  energy: { animated: AnimatedZap, glyph: Zap },
  enter: { animated: AnimatedCornerDownLeft, glyph: CornerDownLeft },
  environmental: { animated: AnimatedHaze, glyph: Haze },
  expand: { animated: AnimatedChevronsLeftRight, glyph: ChevronsLeftRight },
  file: { animated: AnimatedFileText, glyph: FileText },
  "file-check": { animated: AnimatedFileCheck, glyph: FileCheck },
  "file-search": { animated: AnimatedFileSearch, glyph: FileSearch },
  filter: { animated: AnimatedFunnel, glyph: Funnel },
  folders: { animated: AnimatedLayoutList, glyph: LayoutList },
  footprints: { animated: AnimatedFootprints, glyph: Footprints },
  governance: { animated: AnimatedBookmarkCheck, glyph: BookmarkCheck },
  grid: { animated: AnimatedLayoutGrid, glyph: LayoutGrid },
  home: { animated: AnimatedHouse, glyph: House },
  idea: { animated: AnimatedLightbulb, glyph: Lightbulb },
  info: { animated: AnimatedInfo, glyph: Info },
  integration: { animated: AnimatedBoxes, glyph: Boxes },
  invite: { animated: AnimatedMailPlus, glyph: MailPlus },
  key: { animated: AnimatedKeyRound, glyph: KeyRound },
  laptop: { animated: AnimatedLaptop, glyph: Laptop },
  library: { animated: AnimatedBookOpenText, glyph: BookOpenText },
  link: { animated: AnimatedLink, glyph: Link },
  list: { animated: AnimatedList, glyph: List },
  "list-checks": { animated: AnimatedListChecks, glyph: ListChecks },
  live: { animated: AnimatedRocket, glyph: Rocket },
  location: { animated: AnimatedMapPin, glyph: MapPin },
  logout: { animated: AnimatedLogOut, glyph: LogOut },
  mail: { animated: AnimatedMail, glyph: Mail },
  "map-marker": { animated: AnimatedMapPinned, glyph: MapPinned },
  material: { animated: AnimatedBox, glyph: Box },
  message: { animated: AnimatedMessageSquareText, glyph: MessageSquareText },
  "message-add": {
    animated: AnimatedMessageSquarePlus,
    glyph: MessageSquarePlus
  },
  "message-dismiss": {
    animated: AnimatedMessageSquareX,
    glyph: MessageSquareX
  },
  more: { animated: AnimatedEllipsisVertical, glyph: EllipsisVertical },
  "move-row": { animated: AnimatedWrapText, glyph: TextWrap },
  notification: { animated: AnimatedBell, glyph: Bell },
  packages: { animated: AnimatedBringToFront, glyph: BringToFront },
  pending: { animated: AnimatedHourglass, glyph: Hourglass },
  percentage: { glyph: Percent },
  print: { animated: AnimatedPrinter, glyph: Printer },
  refresh: { animated: AnimatedRefreshCw, glyph: RefreshCw },
  reject: { animated: AnimatedFileX, glyph: FileX },
  remove: { animated: AnimatedMinus, glyph: Minus },
  save: { animated: AnimatedSave, glyph: Save },
  scan: { animated: AnimatedScan, glyph: Scan },
  search: { animated: AnimatedSearch, glyph: Search },
  "select-off": { glyph: Circle },
  send: { animated: AnimatedSend, glyph: Send },
  settings: { animated: AnimatedSettings, glyph: Settings },
  share: { animated: AnimatedShare, glyph: Share2 },
  sliders: { animated: AnimatedSlidersHorizontal, glyph: SlidersHorizontal },
  star: { filled: true, glyph: Star },
  "star-outline": { glyph: Star },
  stop: { filled: true, glyph: Square },
  support: { animated: AnimatedHeadset, glyph: Headset },
  transport: { animated: AnimatedCar, glyph: Car },
  "trending-down": { animated: AnimatedTrendingDown, glyph: TrendingDown },
  "trending-up": { animated: AnimatedTrendingUp, glyph: TrendingUp },
  undo: { animated: AnimatedReply, glyph: Reply },
  upload: { animated: AnimatedUpload, glyph: Upload },
  "user-add": { animated: AnimatedUserPlus, glyph: UserPlus },
  "user-check": { animated: AnimatedUserCheck, glyph: UserCheck },
  "user-edit": { animated: AnimatedUserPen, glyph: UserPen },
  "user-settings": { animated: AnimatedUserCog, glyph: UserCog },
  users: { animated: AnimatedUsers, glyph: Users },
  view: { animated: AnimatedEye, glyph: Eye },
  "view-off": { animated: AnimatedEyeOff, glyph: EyeOff },
  water: { animated: AnimatedDroplet, glyph: Droplet },
  webhook: { animated: AnimatedWebhook, glyph: Webhook }
} as const satisfies Record<string, IconEntry>;

export type IconName = keyof typeof ICON_REGISTRY;

export const ICON_NAMES = Object.keys(ICON_REGISTRY).sort() as IconName[];
